import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";

import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Modal } from "bootstrap";
import Cropper from "cropperjs";
import Swal from "sweetalert2";
import { GoogleMapsModule } from "@angular/google-maps";
import { CategoryService } from "../../services/category.service";
import { CompanyService } from "../../services/company.service";
import { ImageService } from "../../services/image.service";
import { StorageService } from "../../services/storage.service";
import { UserService } from "../../services/user.service";

@Component({
  selector: "app-company-profile",
  templateUrl: "./company-profile.component.html",
  styleUrls: ["./company-profile.component.scss"],
  imports: [CommonModule, ReactiveFormsModule, GoogleMapsModule],
})
export class CompanyProfileComponent implements OnInit {
  companyProfileForm!: FormGroup;
  company: any;
  categories: any[] = [];
  cidade: any = "";
  id!: string;
  profileLoaded = false;

  @ViewChild("image", { static: false })
  imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild("fileInput", { static: false })
  fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild("modalCropper") modalElement!: ElementRef;
  @ViewChild("googleMap", { static: false })
  googleMapElement!: ElementRef;

  cropper!: Cropper;
  croppedImage!: string | null;
  modal!: Modal;

  // Google Maps properties
  center: google.maps.LatLngLiteral = { lat: -23.55052, lng: -46.633308 };
  zoom = 13;
  mapOptions: google.maps.MapOptions = {
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 3,
    styles: [
      {
        featureType: "poi.business",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text",
        stylers: [{ visibility: "off" }],
      },
    ],
  };
  markerPosition: google.maps.LatLngLiteral | null = null;

  constructor(
    @Inject(CategoryService) private categoryService: CategoryService,
    @Inject(CompanyService) private companyService: CompanyService,
    @Inject(UserService) private userService: UserService,
    @Inject(HttpClient) private http: HttpClient,
    @Inject(ImageService) private imageService: ImageService,
    @Inject(StorageService) private storageService: StorageService,
    @Inject(UntypedFormBuilder) private formBuilder: UntypedFormBuilder
  ) {}

  async ngOnInit() {
    this.listCategories();
    this.createForm();
    await this.resolverEmpresaDoUsuario();
  }

  private async resolverEmpresaDoUsuario(): Promise<void> {
    try {
      const userJson = localStorage.getItem("user");
      if (!userJson) return;

      const uid: string = JSON.parse(userJson)?.uid;
      if (!uid) return;

      const userRecord = await this.userService.getByUid(uid);
      if (!userRecord?.["companyId"]) return;

      const companyId: string = userRecord["companyId"];

      // Atualiza o localStorage para manter consistência
      localStorage.setItem("_companyId", companyId);

      await this.carregarFormulario(companyId);
    } catch (error) {
      console.error("Erro ao resolver empresa do usuário:", error);
    }
  }

  async carregarFormulario(id: string) {
    this.id = id;
    try {
      const data = await this.companyService.getCompanyById(id);
      if (!data) return;

      this.company = data;

      // Reconstrói o FormArray de telefones com os dados do Firebase
      const telefonesArray = this.companyProfileForm.get("telefones") as import("@angular/forms").FormArray;
      telefonesArray.clear();
      const telefones: string[] = data.telefones && data.telefones.length > 0
        ? data.telefones
        : [""];
      telefones.forEach((tel: string) =>
        telefonesArray.push(
          this.formBuilder.control(tel, [Validators.required])
        )
      );

      // Patch dos demais campos (patchValue ignora campos ausentes no form)
      this.companyProfileForm.patchValue({
        id: data.id,
        cnpj: data.cnpj ?? "",
        nomeFantasia: data.nomeFantasia ?? "",
        razaoSocial: data.razaoSocial ?? "",
        endereco: data.endereco ?? "",
        cep: data.cep ?? "",
        bairro: data.bairro ?? "",
        complemento: data.complemento ?? "",
        numero: data.numero ?? "",
        uf: data.uf ?? "",
        municipio: data.municipio ?? "",
        categoria: data.categoria ?? "",
        foto: data.foto ?? "",
        isOnline: data.isOnline ?? false,
        socials: {
          facebook: data.socials?.facebook ?? "",
          instagram: data.socials?.instagram ?? "",
          linkedin: data.socials?.linkedin ?? "",
          whatsapp: data.socials?.whatsapp ?? "",
        },
        geolocalizacao: {
          lat: data.geolocalizacao?.lat ?? "",
          lng: data.geolocalizacao?.lng ?? "",
          accuracy: data.geolocalizacao?.accuracy ?? "",
        },
      });

      // Posiciona o mapa: usa geo salva se existir, caso contrário geocodifica o endereço
      const geo = data.geolocalizacao;
      if (geo?.lat && geo?.lng) {
        const lat = +geo.lat;
        const lng = +geo.lng;
        this.center = { lat, lng };
        this.markerPosition = { lat, lng };
        this.zoom = geo.accuracy || 15;
      } else {
        this.geocodarEnderecoEmpresa(data);
      }

      this.profileLoaded = true;
    } catch (error) {
      console.error("Erro ao carregar dados da empresa:", error);
      this.profileLoaded = true;
      Swal.fire("Erro", "Não foi possível carregar os dados da empresa.", "error");
    }
  }

  private geocodarEnderecoEmpresa(data: any): void {
    const partes = [
      data.endereco,
      data.numero,
      data.bairro,
      data.municipio,
      data.uf,
      data.cep,
      "Brasil",
    ].filter(Boolean);

    if (partes.length <= 1) return; // só "Brasil" não é suficiente

    const enderecoCompleto = partes.join(", ");

    const tentar = () => {
      if (typeof google === "undefined" || !google.maps?.Geocoder) {
        setTimeout(tentar, 500);
        return;
      }
      new google.maps.Geocoder().geocode(
        { address: enderecoCompleto },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            const location = results[0].geometry.location;
            this.center = { lat: location.lat(), lng: location.lng() };
            this.zoom = 16;
          }
        }
      );
    };

    tentar();
  }

  createForm(data?: any) {
    this.companyProfileForm = this.formBuilder.group({
      id: [data?.id ?? null],
      companyId: [data?.companyId ?? ""],
      cnpj: [data?.cnpj ?? "", Validators.required],
      nomeFantasia: [data?.nomeFantasia ?? "", Validators.required],
      razaoSocial: [data?.razaoSocial ?? "", Validators.required],
      endereco: [data?.endereco ?? "", Validators.required],
      cep: [data?.cep ?? "", Validators.required],
      bairro: [data?.bairro ?? "", Validators.required],
      complemento: [data?.complemento ?? ""],
      numero: [data?.numero ?? "", Validators.required],
      uf: [data?.uf ?? "", Validators.required],
      municipio: [data?.municipio ?? "", Validators.required],
      geolocalizacao: this.formBuilder.group({
        lat: [data?.geolocalizacao?.lat ?? "", Validators.required],
        lng: [data?.geolocalizacao?.lng ?? "", Validators.required],
        accuracy: [data?.geolocalizacao?.accuracy ?? "", Validators.required],
      }),
      categoria: [data?.categoria ?? "", Validators.required],
      socials: this.formBuilder.group({
        facebook: [data?.socials?.facebook ?? ""],
        instagram: [data?.socials?.instagram ?? ""],
        linkedin: [data?.socials?.linkedin ?? ""],
        whatsapp: [data?.socials?.whatsapp ?? ""],
      }),
      foto: [data?.foto ?? ""],
      isOnline: [data?.isOnline ?? false],
      telefones: this.formBuilder.array(
        (data?.telefones && data.telefones.length > 0
          ? data.telefones
          : [""]
        ).map((telefone: string) =>
          this.formBuilder.control(telefone, [
            Validators.required,
            Validators.pattern(/^\+?[1-9]\d{1,14}$/),
          ])
        )
      ),
      status: 1,
    });
  }

  listCategories() {
    this.categoryService.getAll().subscribe((res) => {
      this.categories = res.sort((a, b) =>
        a.description.localeCompare(b.description)
      );
    });
  }

  get hasGeo(): boolean {
    const geo = this.companyProfileForm?.get("geolocalizacao");
    return !!(geo?.get("lat")?.value && geo?.get("lng")?.value);
  }

  get hasCategory(): boolean {
    const cat = this.companyProfileForm?.get("categoria")?.value;
    return !!(cat && String(cat).trim());
  }

  async formSubmit() {
    if (this.companyProfileForm.invalid) {
      Swal.fire(
        "Atenção",
        "Existem campos obrigatórios que não foram preenchidos.",
        "warning"
      );
      return;
    } else {
      const formValue = this.companyProfileForm.value;
      const payload = {
        ...formValue,
        geolocalizacao: {
          lat: Number(formValue.geolocalizacao.lat),
          lng: Number(formValue.geolocalizacao.lng),
          accuracy: Number(formValue.geolocalizacao.accuracy),
        },
      };
      try {
        await this.companyService.update(this.id, payload);

        // Atualiza as flags de perfil para liberar os demais menus
        const hasGeo = !!(payload.geolocalizacao.lat && payload.geolocalizacao.lng);
        const cat = formValue.categoria;
        const hasCategory = !!(cat && String(cat).trim());
        localStorage.setItem("_hasGeo", String(hasGeo));
        localStorage.setItem("_hasCategory", String(hasCategory));

        Swal.fire("Salvo com Sucesso.", "", "success");
      } catch (error) {
        console.error("Erro ao atualizar empresa:", error);
        Swal.fire(
          "Erro!",
          "Não foi possível atualizar os seus dados. Tente novamente mais tarde.",
          "error"
        );
      }
    }
  }

  get telefones(): FormArray {
    return this.companyProfileForm.get("telefones") as FormArray;
  }

  adicionarTelefone(): void {
    const telefoneControl = this.formBuilder.control("", [Validators.required]);
    this.telefones.push(telefoneControl);
  }

  removerTelefone(index: number): void {
    this.telefones.removeAt(index);
  }

  ngAfterViewInit(): void {
    this.modal = new Modal(this.modalElement.nativeElement);

    // Aguardar o Google Maps carregar antes de inicializar
    this.waitForGoogleMaps();
  }

  private waitForGoogleMaps(): void {
    const checkGoogleMaps = () => {
      return typeof google !== "undefined" && google.maps;
    };

    if (checkGoogleMaps()) {
      console.log("Google Maps já está carregado");
      return;
    }

    // Aguardar o Google Maps carregar
    let attempts = 0;
    const maxAttempts = 20; // 10 segundos

    const interval = setInterval(() => {
      attempts++;
      if (checkGoogleMaps()) {
        clearInterval(interval);
        console.log("Google Maps carregado com sucesso");
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        console.error("Google Maps não carregou após 10 segundos");
      }
    }, 500);
  }

  // Métodos para Google Maps
  onMapClick(event: any): void {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      // Atualizar posição do marcador
      this.markerPosition = { lat, lng };

      // Atualizar o formulário com as coordenadas
      this.companyProfileForm.get("geolocalizacao")?.patchValue({
        lat: parseFloat(lat.toFixed(6)),
        lng: parseFloat(lng.toFixed(6)),
        accuracy: this.zoom,
      });
    }
  }

  onZoomChanged(): void {
    // Atualizar a precisão baseada no nível de zoom
    this.companyProfileForm.get("geolocalizacao")?.patchValue({
      accuracy: this.zoom,
    });
  }

  // Métodos para imagem
  openModal() {
    this.croppedImage = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = "";
    }
    this.modal.show();
  }

  closeModal() {
    if (this.modal) {
      this.modal.hide();
      if (this.cropper) {
        this.cropper.destroy();
      }

      if (this.imageElement) {
        this.imageElement.nativeElement.src = "";
      }

      this.croppedImage = null;
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.imageService.resizeImage(file, (resizedImage: HTMLImageElement) => {
        this.imageElement.nativeElement.src = resizedImage.src;
        setTimeout(() => {
          if (this.cropper) {
            this.cropper.destroy();
          }
          this.cropper = new Cropper(this.imageElement.nativeElement, {
            aspectRatio: 1,
            viewMode: 1,
            autoCropArea: 0.8,
            responsive: true,
            scalable: true,
            zoomable: true,
            rotatable: true,
            checkOrientation: true,
            crop: () => {
              const canvas = this.cropper.getCroppedCanvas({
                width: 300,
                height: 300,
              });
              this.croppedImage = canvas.toDataURL("image/png");
            },
          });
        }, 100);
      });
    }
  }

  async uploadImage() {
    if (!this.croppedImage) return;
    const filePath = `profile/${new Date().getTime()}.png`;
    await this.storageService
      .uploadImage(filePath, this.croppedImage)
      .then((res) => {
        const fotoAntiga = this.companyProfileForm.get("foto")?.value;
        this.storageService.deleteImage(fotoAntiga);
        this.companyProfileForm.patchValue({
          foto: res,
        });
        if (this.id) {
          this.companyService.updatePhoto(this.id, res);
        }
        this.modal.hide();
      });
  }
}
